import { safeStringify } from '../util-objects';

/**
 * Error we use in our model. Some of them may be backend error codes, others might not even have an
 * equivalent backend error.
 */
export const ErrorCode = {
	/** the user is not authorized to take this action, with his current token */
	INVALID_SCOPES: 'invalid_scopes',

	/** The user requested to cancel the current operation */
	CANCELLED: 'operation_cancelled_by_mv-web',

	/** There was a server-side error when handling the request */
	SERVER_ERROR: 'server_error',

	/** A bug in this codebase caused the request to fail */
	CLIENT_ERROR: 'client_error',

	/** The requested operation is not legal given the current subscription state */
	ILLEGAL_SUBSCRIPTION_STATE: 'illegal_subscription_state',

	/** The request failed because too many requests were sent to the server */
	REQUEST_THROTTLED: 'request_throttled',

	/** The request failed because the server could not be contacted, or the response could not be read */
	HTTP_CONNECTION_ERROR: 'http_connection_error',

	/** during authorization: the customer is already authorized for this sub */
	CUSTOMER_ALREADY_AUTHORIZED: 'customer_already_authorized',

	/** during authorization: the authorizee is the authorizer */
	CANNOT_AUTHORIZE_SELF: 'customer_should_not_be_self',

	INVALID_AUTHORIZEE: 'invalid_authorizee',

	/** the user is not authenticated */
	INVALID_GRANT: 'invalid_grant',

	/** step-up auth was required, but did not succeed */
	STEP_UP_FAILED: 'step_up_failed',

	/** A passed value is not valid.  This error usually is given on a specific field */
	INVALID_VALUE: 'invalid_value',

	DUPLICATE_VALUE: 'duplicate_value',

	/* Password validation errors */
	PASSWORD_TOO_COMMON: 'password_too_common',
	PASSWORD_ENTIRELY_NUMERIC: 'password_entirely_numeric',
	PASSWORD_TOO_SIMILAR: 'password_too_similar',
	PASSWORD_SIMILAR_TO_DETAILS: 'password_similar_to_details',
	PASSWORD_EXPOSED_IN_DATA_BREACH: 'password_exposed_in_data_breach',

	EMAIL_ADDRESS_ALREADY_IN_USE: 'email_already_in_use',
	MSISDN_ALREADY_IN_USE: 'invalid_mnp_msisdn_in_use',
	EMAIL_NOT_VERIFIED: 'email_not_verified',
	EMAIL_INVALID: 'email_invalid',

	/** The condition for a poll call was not reached before the given timeout */
	POLL_TIMEOUT: 'poll_timeout',

	ORDER_FAILED: 'order_failed',
	ORDER_CANCELLED: 'order_cancelled',

	NOT_ENOUGH_VP: 'insufficient_credit',

	/** The maximum number of bundles for the subscription has been reached */
	MAX_ROAMING_BUNDLE_AMOUNT_REACHED: 'max_roaming_bundle_amount_reached',

	/** The product was ordered too many times this month */
	PRODUCT_ORDERED_TOO_OFTEN: 'product_ordered_too_often',

	PRODUCT_CANNOT_BE_PAID_WITH_CALLING_CREDIT: 'product_cannot_be_paid_with_calling_credit',

	SUBSCRIPTION_TYPE_NOT_SUPPORTED: 'subscription_type_not_supported',

	/* not-found entities */
	NOT_FOUND: 'not_found',
	PRODUCT_NOT_FOUND: 'product_not_found',
	CART_NOT_FOUND: 'cart_not_found',
	ORDER_NOT_FOUND: 'order_not_found',
	CUSTOMER_NOT_FOUND: 'customer_not_found',
	SUBSCRIPTION_CANNOT_BE_FOUND: 'subscription_can_not_be_found',
	EASY_SWITCH_REQUEST_NOT_FOUND: 'easy_switch_request_not_found',

	/** A language change was requested on a customer when the previous one was still being processed */
	OTHER_PENDING_LANGUAGE_CHANGE: 'other_pending_language_change',

	/** The requested operation can only be done after at least one invoice was paid */
	NOT_ALLOWED_WITHOUT_PAID_INVOICE: 'not_allowed_without_paid_invoice',

	/** The requested operation cannot be done because an invoice is unpaid */
	NOT_ALLOWED_BECAUSE_DUNNING: 'action_not_authorized_due_to_dunning',

	/** The requested operation cannot be done because an invoice is unpaid */
	NOT_ALLOWED_BECAUSE_UNPAID_INVOICES: 'not_allowed_with_unpaid_invoices',

	/** The requested operation cannot be done because an invoice is unpaid (cleanup needed here) */
	REQUEST_SIM_BLOCKED_DUE_UNPAID_INVOICES: 'request_sim_blocked_due_unpaid_invoices',

	/** The requested operation cannot be done because an invoice is unpaid (cleanup needed here) */
	TRANSFER_NOT_ALLOWED_WITH_OPEN_INVOICES: 'transfer_not_allowed_with_open_invoices',

	/** The requested operation cannot be done because a b2b invoice is unpaid */
	B2B_COMPANY_IN_DUNNING: 'b2b_company_in_dunning',

	/** The requested operation cannot be done because another process is in the way */
	OTHER_PROCESS_PENDING: 'other_process_pending',

	/** The requested operation cannot be cancelled */
	PROCESS_CANNOT_BE_CANCELLED: 'process_cannot_be_cancelled',

	/** The requested operation cannot be done because a migration is in the way (pending) */
	MIGRATION_PENDING: 'migration_pending',

	/** The requested operation cannot be done because a migration is in the way (ongoing) */
	MIGRATION_ALREADY_RUNNING: 'migration_already_running',

	/** User is migrating too often.  calm down. */
	TOO_MANY_MIGRATIONS: 'switching_plans_too_frequently',

	/** Cannot migrate before first invoice is paid */
	SWITCHING_BLOCKED_FIRST_PAYMENT_REQUIRED: 'switching_blocked_first_payment_required',

	/** User migrated too recently.  calm down. */
	ALREADY_SWITCHED_TOO_RECENTLY: 'already_switched_within_short_timeframe',

	/** The given MGM referral is invalid */
	INVALID_REFERRED_MGM_CUSTOMER: 'invalid_referred_mgm_customer',

	/** During refresh: The token used for authenticating is invalid */
	INVALID_TOKEN: 'invalid_token',

	/** During refresh: The token used for authenticating is not a refresh token */
	NO_REFRESH_TOKEN: 'no_refresh_token',

	/** During refresh: another process held the auth refresh lock until acquire timed out */
	AUTH_REFRESH_LOCK_BUSY: 'auth_refresh_lock_busy',

	/** During refresh: the work under the auth refresh lock exceeded its execution deadline */
	AUTH_REFRESH_LOCK_CALLBACK_TIMEOUT: 'auth_refresh_lock_callback_timeout',

	/** During login: The user cancelled the login attempt */
	LOGIN_ABORTED: 'login_aborted',

	/** During auto-payment: The auto-payment is set for authorized sim that is in grouped invoicing */
	NOT_ALLOWED_SUBSCRIPTION_IN_INVOICE_GROUP: 'not_allowed_subscription_in_invoice_group',

	/** During sepa: the given iban is used by someone else */
	IBAN_ALREADY_EXISTS: 'iban_already_exists',

	/** During sepa: the given iban is not supported */
	IBAN_COUNTRY_UNSUPPORTED: 'mandate_cannot_be_set_invalid_values',

	/** During sepa: the mandate cannot be created for the given account */
	MANDATE_CANNOT_BE_SET: 'mandate_cannot_be_set',

	/** During sepa: the given iban is already used by user himself */
	IBAN_ALREADY_LINKED_TO_YOUR_ACCOUNT: 'iban_already_linked_to_your_account',

	/** During sepa: the given iban and bic don't match up */
	DEBTOR_BIC_IBAN_MISMATCH: 'debtor_bic_iban_mismatch',

	/** During sepa: the given iban and bic don't match up */
	DEBTOR_BIC_VALIDATION_FAILED: 'bic_not_reachable',

	/** During sepa: cannot setup a sepa when another one is pending */
	UNCONFIRMED_SEPA_MANDATE_PENDING: 'unconfirmed_sepa_mandate_pending',

	/** The requested amount of viking points is too high. */
	INSUFFICIENT_LOYALTY_POINTS: 'insufficient_loyalty_points',

	/** User has insufficient prepaid credit to perform the operation. */
	INSUFFICIENT_CREDIT: 'insufficient_credit',

	/** The requested action cannot be performed on the given subscription because it is not active */
	SUBSCRIPTION_DEACTIVATED: 'subscription_deactivated',

	/** The requested action is not authorized by the current user */
	ACTION_NOT_AUTHORIZED: 'action_not_authorized',

	/** the order that was requested to be paid is not in a payable state */
	NOT_PAYABLE: 'not_payable',

	/** various errors depending on situation.  This needs fixing. */
	UNABLE_TO_PROCESS: 'unable_to_process',

	/** Recpatcha failed because of a technical error */
	RECAPTCHA_TECHNICAL_ERROR: 'recaptcha_technical_error',

	/** The used recaptcha has expired.  Get a new one */
	RECAPTCHA_EXPIRED: 'recaptcha_expired',

	/** During activation: The sim that was requested to be activated cannot be activated yet */
	SIM_NOT_IN_CORRECT_STATE_FOR_ACTIVATION: 'sim_not_in_correct_state_for_activation',

	/** During activation: The sim that was requested to be activated is already activated */
	SIM_ALREADY_ACTIVE: 'sim_already_active',

	/** During activation: The sim that was requested to be activated is already being activated */
	ACTIVATION_ONGOING: 'activation_ongoing',

	/** The requested subscription cannot be acted upon by the current user */
	NO_AUTHORIZATION_ON_SUBSCRIPTION: 'no_authorization_on_subscription',

	/** This account cannot log in using email.  Use phone login instead */
	LOGIN_BY_EMAIL_IMPOSSIBLE: 'login_by_email_impossible',

	/** The requested action required a verified identity.  Verify identity first */
	NO_IDENTITY_VERIFICATION: 'not_allowed_as_unidentified_customer',

	/** During b2b joining: MobileSubscription is already linked to an employer */
	SUBSCRIPTION_ALREADY_IS_LINKED_TO_AN_EMPLOYER: 'subscription_already_linked_to_employer',

	/** During b2b joining: MobileSubscription is not eligible for b2b */
	SUBSCRIPTION_IS_NOT_ELIGIBLE_FOR_B2B: 'product_not_eligible',

	/** During clan invite: MobileSubscription is already in a clan */
	SUBSCRIPTION_ALREADY_IS_MEMBER_OF_ANOTHER_CLAN: 'subscription_already_is_member_of_another_clan',

	/** During clan invite: MobileSubscription is not in the state to join a clan */
	INCORRECT_SUBSCRIPTION_STATE_FOR_INVITE: 'incorrect_subscription_state_for_invite',

	/** During clan join/invite and trial clan activation: subscription is not eligible for clan */
	NOT_ELIGIBLE_TO_JOIN: 'not_eligible_to_join',

	/** During clan join/invite: clan is full */
	MAX_AMOUNT_OF_CLAN_MEMBERS_REACHED: 'max_amount_of_members_reached',

	/** During clan join/invite: clan cannot add preexisting members */
	MAX_AMOUNT_OF_PRE_EXISTING_CLAN_MEMBERS_REACHED: 'max_amount_of_pre_existing_members_reached',

	/** During clan join/invite: user left clan too recently */
	LEFT_ANOTHER_CLAN_TOO_RECENTLY: 'left_another_clan_too_recently',

	/** Something went wrong that only care can fix */
	CUSTOMER_SHOULD_CONTACT_CARE: 'customer_should_contact_care',

	/** When geocoding: address not found */
	GEOCODER_ADDRESS_NOT_FOUND: 'geocoder_address_not_found',

	/** When geocoding: address not in Belgium */
	GEOCODER_ADDRESS_NOT_IN_BELGIUM: 'geocoder_address_not_in_belgium',

	/** When geocoding: address not complete enough for geolocation */
	GEOCODER_ADDRESS_INCOMPLETE: 'geocoder_address_incomplete',

	/** Technical error: the process was expecting to have a product, but none was present */
	ORDER_HAS_NO_PRODUCT: 'order_has_no_product',

	/** The given EID is not valid */
	EID_INVALID: 'eid_invalid',

	/** When making a deals claim: a claim was already registered for this click */
	CLAIM_ALREADY_EXISTS_LEGACY: 'Claim already exists',
	CLAIM_ALREADY_EXISTS: 'claim_already_exists',

	/** When trying to create a second no-click claim */
	NEEDS_CLICK: 'needs_click',

	/** When a viking without any active subscriptions is trying to make a claim */
	ACCOUNT_INELIGIBLE: 'account_ineligible',

	/** The viking does not have a click on the moment they submitted a claim for,  */
	/** or the selected historical deal was not active at that time */
	INVALID_CLICK_TIME: 'invalid_click_time',

	/** The historical deal the viking selected is not owned by the selected partner */
	INVALID_PARTNER: 'invalid_partner',

	/** The selected partner does not accept any claims */
	PARTNER_NOT_ALLOWED: 'partner_not_allowed',

	/** The selected historical deal is not known to the system */
	INVALID_DEAL_HISTORY: 'invalid_deal_history',

	/** The given company number was valid, but now known */
	UNKNOWN_COMPANY_NUMBER: 'unknown_company_number',

	/** The given company number was invalid */
	INVALID_ENTERPRISE_NUMBER: 'invalid_enterprise_number',

	/** The given company number is in use */
	ENTERPRISE_NUMBER_ALREADY_IN_USE: 'enterprise_number_already_in_use',

	/** During swap request: a swap is already in progress */
	SWAP_ALREADY_RUNNING: 'swap_already_running',

	/** During company creation: user already has a company */
	ALREADY_HAS_A_COMPANY: 'already_has_a_company',

	/** The requested operation is not allowed for minors */
	NOT_POSSIBLE_FOR_MINORS: 'not_possible_for_minors',

	/** The given value is already in use.  Normally shows up on fields */
	VALUE_ALREADY_IN_USE: 'value_already_in_use',

	/** The given email address uses a blacklisted domain */
	EMAIL_DOMAIN_NOT_ALLOWED: 'email_domain_not_allowed',

	/** When activating a viking deal: We're out of vouchers.  You lose. */
	OUT_OF_VOUCHERS: 'OUT_OF_VOUCHERS',

	/** during esim swap: the user is not identified */
	ESIM_SWAP_REQUIRES_IDENTIFICATION: 'esim_swap_requires_identification',

	/** Field error: a field that is required eas missing */
	REQUIRED: 'required',

	/** During sim resend: resend was not possible at this time */
	SIM_RESEND_NOT_POSSIBLE: 'resend_not_possible',

	/** This operation was blocked because the current user is legally incompetent */
	LEGALLY_INCOMPETENT: 'legally_incompetent',

	/** Any Mollie payment needs to be at least 2 eurocent */
	RESIDUAL_AMOUNT_TOO_LOW: 'residual_amount_too_low',

	/** During sim transfer: the given iccid does not match the given msisdn */
	ICCID_NOT_CORRECT_FOR_TRANSFER: 'iccid_not_correct_for_transfer',

	/** User tries to activate a trial clan with a token that is not valid for their account */
	TRIAL_TOKEN_DOES_NOT_BELONG_TO_CUSTOMER: 'trial_token_does_not_belong_to_customer',

	/** New subscriptions can't double dip by doing MGM and then joining a clan */
	CANT_JOIN_CLAN_WITH_NEW_SUBSCRIPTION_THAT_DID_MGM: 'new_subscription_with_mgm_referral_not_allowed',

	/** Prior to creating employees, you need set a mandate */
	NO_MANDATE_SET: 'no_mandate_set',

	/** Attempt to accept/reject an invitation when it was not in a state to do so */
	AUTH_INVITE_IN_INCORRECT_STATE: 'authorization_invitation_not_in_correct_state',

	/** Attempt to book an appointment on an order that does not expect it */
	ORDER_NOT_IN_CORRECT_STATE_FOR_APPOINTMENT: 'order_not_in_correct_state_for_appointment',

	/** The order never reached the correct state after polling */
	ORDER_NEVER_REACHED_CORRECT_STATE_FOR_APPOINTMENT: 'order_never_reached_correct_state_for_appointment',

	/** The selected timeslot is no longer available by the time it got submitted */
	TIMESLOT_NO_LONGER_AVAILABLE: 'timeslot_no_longer_available',

	/** Can't book right now.  Viking should come back later */
	APPOINTMENT_SCHEDULING_NOT_POSSIBLE_TRY_LATER: 'appointment_scheduling_not_possible_try_later',

	/** Can't book right now, viking will be mailed when booking becomes possible */
	APPOINTMENT_SCHEDULING_NOT_POSSIBLE_MANUAL_HANDLING: 'appointment_scheduling_not_possible_wait_for_info',

	/** The requested product is not available for the given address */
	UNSUPPORTED_PRODUCT_FOR_ADDRESS: 'product_not_available_on_location',

	/** We can't guarnatee sufficient speed at the given address */
	SPEED_TOO_LOW: 'speed_too_low',

	/** The given address is invalid */
	INVALID_ADDRESS: 'invalid_address',

	/** The given address does not have the necessary infrastructure because it was not allowed by the Syndic */
	SYNDICUS_CONTACT_NEEDED: 'syndicus_contact_needed',

	/** When onboarding without a reduction code but you need one to be eligible */
	REDUCTION_CODE_REQUIRED: 'reduction_code_required',

	ADDING_EMPLOYEE_NOT_ALLOWED_DUE_TO_DUNNING: 'adding_employee_not_allowed_because_of_dunning_level',

	/** When a GET call doesn't include at least one required parameter */
	MISSING_QUERY_PARAMETERS: 'missing_query_parameters',

	/** When a GET call doesn't include at least one required parameter (yes the backend error code is singular) */
	INVALID_QUERY_PARAMETERS: 'invalid_query_parameter',

	/** Uses on field level to indicates fields that are required */
	FIELD_IS_REQUIRED: 'required',

	/** WIP gives an error on the line speed check */
	WIP_UNAVAILABLE_FOR_LINE_SPEED_CHECK: 'wip_unavailable_for_line_speed_check',

	/** A repair is requested for a line for which there already is one */
	REPAIR_ALREADY_EXISTS: 'repair_request_already_exists',

	/** When the type of a file submitted by the user is not supported */
	FILE_TYPE_NOT_SUPPORTED: 'file_type_not_supported',

	/** When a file submitted by the user is not readable */
	FILE_NOT_READABLE: 'file_not_readable',

	INVALID_FIELDS: 'invalid_fields',

	NO_SUCH_COMPENSABLE_ORDER: 'no_such_compensable_order',

	MODEM_SETTINGS_UNAVAILABLE: 'modem_settings_unavailable',

	ORDER_NOT_ACTIVATABLE: 'order_not_activatable',

	INVALID_MNP_ICCID_ALREADY_OWNED: 'invalid_mnp_iccid_already_owned',

	/** An action cannot be taken because the product this viking is set ot migrate to is not compatible with the action */
	PLANNED_MIGRATION_TO_NON_ELIGIBLE_PRODUCT: 'planned_migration_to_non_eligible_product',

	NEW_SUBSCRIPTION_WITH_MGM_REFERRAL_NOT_ALLOWED: 'new_subscription_with_mgm_referral_not_allowed',

	PAYCONIQ_LINKING_FAILED: 'payconiq_linking_failed',

	ORDER_ALREADY_PENDING_FOR_ADDRESS: 'order_already_pending_for_address',

	STUCK_BOOSTER_ORDER_PENDING: 'stuck_booster_order_pending',

	/** The maximum number of gift cards would be reached by placing this order. */
	MAX_GIFT_CARDS_REACHED: 'max_gift_cards_reached',

	/** The maximum gift card amount for the period would be reached by placing this order. */
	MAX_GIFT_CARD_AMOUNT_FOR_PERIOD_REACHED: 'max_gift_card_amount_for_period_reached',

	/* The requested action cannot be performed because of too many unpaid invoices. */
	TOO_MANY_UNPAID_INVOICES: 'too_many_unpaid_invoices',

	/* The requested action cannot be performed. The requestor should check their e-mail for next steps. */
	CUSTOMER_SHOULD_CHECK_THEIR_MAIL: 'customer_should_check_their_mail',

	SWITCHING_BLOCKED_DUE_UNPAID_INVOICES: 'switching_blocked_due_unpaid_invoices',
	TIMEOUT: 'timeout',
	ACCESS_DECLINED: 'declined',
	EXTERNAL_SERVICE_UNAVAILABLE: 'external_service_unavailable',
	IDENTIFIER_ALREADY_USED: 'identifier_already_used',
	UNKNOWN: 'UNKNOWN',

	OWNER_CANT_CONFIRM_OWN_INVITE: 'owner_cant_confirm_own_invite',
	SERVICE_ALREADY_ACTIVE: 'service_already_active',
} as const;

export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];

export default class StructuredError extends Error {
	public static forString(code: string | string[], fallback: ErrorCode, payload?: any): StructuredError {
		if (code instanceof Array) code = code[0]!;

		const errorValues: string[] = Object.keys(ErrorCode).map((key) => ErrorCode[key as keyof typeof ErrorCode]);
		const errorCode = errorValues.includes(code) ? (code as ErrorCode) : fallback;

		return new StructuredError(errorCode, payload);
	}

	public static validationFailed(field: string, code: ErrorCode) {
		return new StructuredError(ErrorCode.INVALID_VALUE, {
			incorrect_fields: [{ field, errors: [{ code, message: 'invalid value' }] }] satisfies FieldErrors,
		});
	}

	public static of(error: unknown, message?: string): StructuredError {
		if (message) {
			const result = StructuredError.of(error);
			result.message = `${message}: ${result.message}`;
			return result;
		}
		if (error instanceof StructuredError) return error;
		if (!(error instanceof Error)) {
			return new StructuredError(ErrorCode.CLIENT_ERROR, safeStringify(error));
		}
		const response = new StructuredError(ErrorCode.CLIENT_ERROR, error.message);
		response.stack = error.stack;
		return response;
	}

	public readonly code: ErrorCode;
	public readonly payload: any;

	constructor(code: ErrorCode, payload?: any) {
		super(payload ? safeStringify(payload) : code);
		Object.setPrototypeOf(this, new.target.prototype);
		this.code = code;
		this.payload = payload;
	}

	fieldCodes(field: string): ErrorCode[] {
		if (!this.payload) return [];
		if (!this.payload.incorrect_fields) return [];
		for (const incorrectField of this.payload.incorrect_fields as FieldErrors) {
			if (incorrectField.field === field) return incorrectField.errors.map((e) => e.code);
		}
		return [];
	}

	fieldCode(field: string): ErrorCode | undefined {
		return this.fieldCodes(field)[0];
	}
}

export class RequestThrottledError extends StructuredError {
	public readonly timeoutSeconds: number | undefined;

	constructor(timeoutSeconds: number | undefined) {
		super(
			ErrorCode.REQUEST_THROTTLED,
			timeoutSeconds ? `Request was throttled for ${timeoutSeconds} seconds` : 'Request was throttled',
		);
		this.timeoutSeconds = timeoutSeconds;
		Object.setPrototypeOf(this, new.target.prototype);
	}
}

type FieldErrors = {
	field: string;
	errors: { code: ErrorCode; message: string }[];
}[];
